import "./update.scss";
import Sidebar from "../../components/sidebar/Sidebar";
import Select from "react-select";
import Navbar from "../../components/navbar/Navbar";
import DriveFolderUploadOutlinedIcon from "@mui/icons-material/DriveFolderUploadOutlined";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { halfleaveformInputs } from "../../formSource";
import { serverUrl } from "../../temp";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const statusOptions = [
  { value: 1, label: "Active" },
  { value: 0, label: "Inactive" },
];

const isImage = (file) => {
  if (!file) return false;
  if (file instanceof File) return file.type.startsWith("image/");
  if (typeof file === "string")
    return [".jpg", ".jpeg", ".png"].some((ext) =>
      file.toLowerCase().endsWith(ext)
    );
  return false;
};

const isPDF = (file) => {
  if (!file) return false;
  if (file instanceof File) return file.type === "application/pdf";
  if (typeof file === "string") return file.toLowerCase().endsWith(".pdf");
  return false;
};

const validateTime = (dateTimeString) => {
  if (!dateTimeString) return false;
  const date = new Date(dateTimeString);
  const hours = date.getHours();
  return hours >= 16 && hours <= 21; // 4 PM to 9 PM
};

const HalfLeaveFormUpdate = ({ title }) => {
  const location = useLocation();
  const LeaveformId = location.pathname.match(
    /\/update-half-leave-form\/(\d+)/
  )?.[1];
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const [inputValues, setInputValues] = useState({
    type: 0, // Half leave type
    Status: 1, // Default to active
  });
  const [token] = useState(localStorage.getItem("token"));
  const [minEndDate, setMinEndDate] = useState("");
  const navigate = useNavigate();

  const redirectToLogin = () => {
    toast.warning("Please login first to access this page", {
      autoClose: 2000,
      onClose: () => (window.location.href = "/"),
    });
  };

  useEffect(() => {
    const fetchLeaveForm = async () => {
      try {
        const response = await fetch(
          `${serverUrl}/student/leave_form/${LeaveformId}`
        );
        if (!response.ok) throw new Error("Failed to fetch leave form");

        const data = await response.json();
        if (data.code !== "200") {
          toast.error(data.message || "Failed to load leave form");
          return;
        }

        setInputValues(data.data);
        setFilePreview(data.data.leave_file_url);
        setMinEndDate(data.data.start_date_time);

        setSelectedStatus(
          data.data.Status == 1
            ? { value: 1, label: "Active" }
            : { value: 0, label: "Inactive" }
        );
      } catch (error) {
        toast.error("Error fetching leave form data");
        console.error(error);
      }
    };

    if (LeaveformId) fetchLeaveForm();
  }, [LeaveformId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "start_date_time") {
      if (!validateTime(value)) {
        toast.error("Leave time must be between 4 PM to 9 PM");
        return;
      }
      setMinEndDate(value);
      setInputValues((prev) => ({
        ...prev,
        [name]: value,
        end_date_time: value, // For half leave, end time same as start time
      }));
      return;
    }

    if (name === "end_date_time") {
      if (!validateTime(value)) {
        toast.error("Leave time must be between 4 PM to 9 PM");
        return;
      }
      if (new Date(value) < new Date(inputValues.start_date_time)) {
        toast.error("End time cannot be before start time");
        return;
      }
    }

    setInputValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!isImage(selectedFile) && !isPDF(selectedFile)) {
      toast.error("Please upload an image (JPEG, PNG) or PDF file");
      return;
    }

    setFile(selectedFile);
    setFilePreview(
      isImage(selectedFile)
        ? URL.createObjectURL(selectedFile)
        : "/images/pdf_icon.png"
    );
  };

  const handleStatusSelectChange = (selectedOption) => {
    setSelectedStatus(selectedOption);
    setInputValues((prev) => ({ ...prev, Status: selectedOption.value }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const toastId = toast.loading("Updating half leave form...");

    if (
      !validateTime(inputValues.start_date_time) ||
      !validateTime(inputValues.end_date_time)
    ) {
      toast.update(toastId, {
        render: "Leave time must be between 4 PM to 9 PM",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
      return;
    }

    const formData = new FormData();
    const adminData = JSON.parse(localStorage.getItem("admindata"));

    formData.append("center_id", localStorage.getItem("center_id"));
    formData.append("student_id", localStorage.getItem("student_id"));
    formData.append("user_id", adminData.id);
    formData.append("reason", inputValues.reason);
    formData.append("start_date_time", inputValues.start_date_time);
    formData.append("end_date_time", inputValues.end_date_time);
    formData.append("type", 0); // Half leave type
    if (file) formData.append("logo", file);
    formData.append("status", inputValues.Status);

    try {
      const response = await fetch(
        `${serverUrl}/student/upd_leave_form/${LeaveformId}`,
        {
          method: "PUT",
          body: formData,
        }
      );

      const data = await response.json();

      if (data.code >= 400) {
        toast.update(toastId, {
          render: data.message || "Failed to update leave form",
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
        return;
      }

      toast.update(toastId, {
        render: "Half leave form updated successfully!",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });

      setTimeout(() => navigate("/student-attendance/half-leave-form"), 2000);
    } catch (error) {
      toast.update(toastId, {
        render: "An error occurred while updating the form",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
      console.error(error);
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      {!token && redirectToLogin()}
      {token && (
        <div className="update">
          <Sidebar />
          <div className="updateContainer">
            <Navbar />
            <div className="top">
              <h1>{title}</h1>
            </div>
            <div className="bottom">
              <div className="left">
                <div className="filePreview">
                  {filePreview ? (
                    isPDF(file || inputValues.leave_file_url) ? (
                      <img
                        src="/images/pdf_icon.png"
                        alt="Leave Form PDF"
                        className="itemImg"
                      />
                    ) : (
                      <img
                        src={filePreview}
                        alt="Leave Form Preview"
                        className="itemImg"
                      />
                    )
                  ) : (
                    <img
                      src="/images/upload_file.png"
                      alt="Default File"
                      className="itemImg"
                    />
                  )}
                  {file ? (
                    <p className="fileName">{file.name}</p>
                  ) : inputValues.leave_file_url ? (
                    <p className="fileName">
                      {inputValues.leave_file_url.split("/").pop()}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="right">
                <form onSubmit={handleUpdate}>
                  <div className="formInput">
                    <label htmlFor="file">
                      Leave Form (Image/PDF):{" "}
                      <DriveFolderUploadOutlinedIcon className="icon" />
                    </label>
                    <input
                      type="file"
                      id="file"
                      accept="image/*,.pdf"
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                    />
                  </div>
                  {halfleaveformInputs.map((input) => (
                    <div className="formInput" key={input.id}>
                      <label>{input.label}</label>
                      {input.fieldName === "status" ? (
                        <Select
                          options={statusOptions}
                          name={input.fieldName}
                          value={selectedStatus}
                          onChange={handleStatusSelectChange}
                          required
                        />
                      ) : (
                        <input
                          type={input.type}
                          placeholder={input.placeholder}
                          name={input.fieldName}
                          value={inputValues[input.fieldName] || ""}
                          onChange={handleInputChange}
                          required
                          min={
                            input.fieldName === "end_date_time"
                              ? minEndDate
                              : undefined
                          }
                        />
                      )}
                    </div>
                  ))}
                  <div style={{ clear: "both" }} className="formUpdate">
                    <button type="submit" style={{ float: "right" }}>
                      Update
                    </button>
                    <button
                      type="button"
                      style={{ float: "right", marginRight: "10px" }}
                      onClick={() =>
                        navigate("/student-attendance/half-leave-form")
                      }
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HalfLeaveFormUpdate;
